import { AppBar, Tab, Tabs, Typography } from '@mui/material';
import produce from 'immer';
import isEqual from 'lodash.isequal';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  dimensionToDataFieldWithDataType,
  toAdvancedDimension,
} from '../../../../common-types';
import i18n from '../../../../i18n';
import {
  ApiDataQueryType,
  ApiDataViewQueryWithTypeInfo,
  ApiMasterDataAdvancedQuery,
  ApiMasterDataAdvancedQueryDimension,
  ApiMasterDataField,
  ApiMasterDataQueryOrderBy,
  ApiMasterDataQueryResponse,
  ApiMasterDataQuerySortOrder,
  ApiMasterDataTypes,
  UndefinedDisplayKey,
} from '../../api/api-interfaces';
import { CachedMasterDataService } from '../../api/cached-master-data-service';
import { appCache } from '../../cache/cache';
import { dateManagerService } from '../../date-manager/date-manager-service';
import { ExportStore } from '../../export-store';
import { PRESENT_TEMPORALITY_FILTER } from '../../filter/common-filters';
import { combineFilters, getVersionFilterFromDate } from '../../filter/utils';
import { useTabs } from '../../hooks/useTabs';
import { ReportStore } from '../../report/report-store';
import { GetResponseForAdvancedQueryService } from '../../services/GetResponseForQueryService';
import { GetResponsesForAdvancedQueriesService } from '../../services/GetResponsesForAdvancedQueriesService';
import { getServiceCacheKey, Service } from '../../services/utils';
import {
  Currency,
  formatApiMasterDataProperty,
  formatDataPoint,
} from '../../utilFunctions/formatters';
import { ApiAlias } from '../../v2/api/alias/types';
import { getAliasForDataField } from '../../v2/api/alias/utils';
import { useListDomainViewDashboards } from '../../v2/api/dashboards/hooks';
import { VersionId } from '../../v2/api/types';
import { BetterLoadingOverlay } from '../../v2/common/components/BetterLoading';
import {
  Dashboard,
  Dashboards,
} from '../../v2/common/components/dashboards/types';
import { Languages } from '../../v2/constants';
import {
  useAliasesContext,
  useGlobalFilterContext,
  useGlobalLocaleContext,
  useLatestEmployeeVersionContext,
  useLatestVersionsContext,
  useLegacyDomainScopedStoresContext,
  useLegacyStoresContext,
} from '../../v2/context/contexts';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../v2/context/legacy-types';
import {
  ApiMasterDataQueryFilterItem,
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../v2/types';
import { getMomentDate } from '../../v2/utils-date';
import QueryExecutor, { RenderCallback } from '../query/QueryExecutor';
import ServiceExecutorWithRoles from '../query/ServiceExecutorWithRoles';
import ReportView from '../ReportView/ReportView';

// Note: Later report query needs to be stored or fetched from somewhere else

const ReportContainer = styled.div`
  height: 100%;
  min-width: 90vw;
  background-color: white;
`;

const StyledTypography = styled(Typography)`
  color: #00a599;
  text-align: center;
`;

const StyledAppBar = styled(AppBar)`
  z-index: 1;
  max-height: 48px;
`;

interface DashboardDataViewProps {
  config?: DataViewConfigWithType;
  dashboard: Dashboards;
}

enum DataViewConfigType {
  DataViewConfigWithTabs = 'DataViewConfigWithTabs',
  DataViewConfigSingleView = 'DataViewConfigSingleView',
}

export type DataViewConfigWithType =
  | {
      type: DataViewConfigType.DataViewConfigWithTabs;
      dataViewConfig: DataViewConfigWithTabs;
    }
  | {
      type: DataViewConfigType.DataViewConfigSingleView;
      dataViewConfig: DataViewConfigSingleView;
    };

export const toDataViewConfigSingleViewWithTypeInfo: (
  queryWithType: ApiDataViewQueryWithTypeInfo,
  sortOrder?: DataFields[] | ApiMasterDataField[],
  useInfiniteLoader?: boolean
) => DataViewConfigWithType = (
  queryWithType: ApiDataViewQueryWithTypeInfo,
  sortOrder?: DataFields[] | ApiMasterDataField[],
  useInfiniteLoader?: boolean
) => ({
  type: DataViewConfigType.DataViewConfigSingleView,
  dataViewConfig: { queryWithType, sortBy: sortOrder, useInfiniteLoader },
});

export const toDataViewConfigWithTabsWithTypeInfo: (
  tabConfigs: TabConfig[],
  defaultTabIndex?: number,
  onTabChange?: (value: number) => void
) => DataViewConfigWithType = (
  tabConfigs: TabConfig[],
  defaultTabIndex?: number,
  onTabChange?: (value: number) => void
) => ({
  type: DataViewConfigType.DataViewConfigWithTabs,
  dataViewConfig: { tabConfigs, onTabChange, defaultTabIndex },
});

const getQueryWithSortParamsForSingleView = (
  configValue: DataViewConfigSingleView,
  sortConfig: SortConfig
): DataViewConfigSingleView => {
  return produce(configValue, (draft) => {
    const orderByDimensions: ApiMasterDataQueryOrderBy[] | undefined =
      configValue.sortOrder?.map((sortOrder) => ({
        dimension: toAdvancedDimension(sortOrder),
        sortOrder: sortConfig.sortOrder,
      })) ??
      (sortConfig.sortBy
        ? [
            {
              dimension: toAdvancedDimension(sortConfig.sortBy),
              sortOrder: sortConfig.sortOrder,
            },
          ]
        : undefined) ??
      configValue.queryWithType.query.dimensions?.map((d) => ({
        dimension: d,
        sortOrder: sortConfig.sortOrder,
      })) ??
      [];
    draft.queryWithType.query.orderBy = orderByDimensions;
  });
};

const getQueriesWithSortParamsForTabsView = (
  tabConfig: TabConfigWithQueries,
  sortConfig: SortConfig
): TabConfigWithQueries => {
  return produce(tabConfig, (draft) => {
    draft.queriesWithType.forEach((query) => {
      const orderByField: ApiMasterDataAdvancedQueryDimension = ((tabConfig
        .sortOrder?.[0]
        ? toAdvancedDimension(tabConfig.sortOrder?.[0])
        : undefined) ||
        (sortConfig.sortBy
          ? toAdvancedDimension(sortConfig.sortBy)
          : undefined) ||
        query.query.dimensions?.[0]) as ApiMasterDataAdvancedQueryDimension;
      query.query.orderBy = [
        {
          dimension: orderByField,
          sortOrder: sortConfig.sortOrder,
        },
      ];
    });
  });
};

const exportDataViewData = (
  fullDataResponse: ExportData,
  dashboard: Dashboards,
  allDashboards: Dashboard[],
  aliases: ApiAlias[],
  selectedLanguage: Languages,
  reportStore: ReportStore,
  exportStore: ExportStore
) => {
  // TODO: Maybe later change the below code into a hook called useGetDashboard
  const dashboardConfig = allDashboards.find((db) => db.id === dashboard);
  const dashboardName = dashboardConfig
    ? i18n.t(dashboardConfig.nameKey)
    : dashboard;
  const date = dateManagerService.getInstance().formatDateApi(getMomentDate());
  const exportFileName = i18n.t('common:dataView.export.filename', {
    dashboardName: `${dashboardName.replace(' ', '_')}`,
    date,
  });
  return exportData(
    fullDataResponse,
    exportFileName,
    aliases,
    selectedLanguage,
    reportStore,
    exportStore
  );
};

const exportData = (
  fullDataResponse: ExportData,
  filename: string,
  aliases: ApiAlias[],
  selectedLanguage: Languages,
  reportStore: ReportStore,
  exportStore: ExportStore
) => {
  const { reportFieldsOrder: reportFieldsWithDataTypeOrder } = reportStore;
  const reportFieldsOrder = reportFieldsWithDataTypeOrder.map(
    (f) => f.dataField
  );
  const headers: DataFieldWithDataType[] =
    fullDataResponse.dataPoints[0]?.dimensions
      ?.map((dim) => ({
        dataType: dim.dataType as DataTypes,
        dataField: dim.property as DataFields,
      }))
      ?.filter(
        (d) =>
          !isEqual(d, {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.VERSION_ID,
          })
      )
      ?.sort(
        (a: DataFieldWithDataType, b: DataFieldWithDataType) =>
          reportFieldsOrder.indexOf(a.dataField) -
          reportFieldsOrder.indexOf(b.dataField)
      ) ?? []; //TODO: confirm that this is okay as a sorting logic

  const reportData = fullDataResponse.dataPoints.map((dataPoint) => {
    return headers.map((h) => {
      const field = dataPoint.dimensions.find(
        (d) => d.dataType === h.dataType && d.property === h.dataField
      );
      return field?.value?.toString() ?? i18n.t(UndefinedDisplayKey);
    });
  });
  const translatedHeaders = headers.map((h) =>
    formatApiMasterDataProperty(
      { property: h },
      getAliasForDataField(aliases, selectedLanguage)(h)
    )
  );

  const exportedData = {
    headers: translatedHeaders,
    data: reportData,
  };
  exportStore.exportReport(filename, exportedData);
};

const getFullDataForExportFromQuery = async (
  query: ApiMasterDataAdvancedQuery,
  cachedMasterDataApiService: CachedMasterDataService,
  referenceCurrency: Currency | null
): Promise<ExportData> => {
  const { limit, offset, ...queryWithoutOffsetAndLimit } = query;
  const fullDataResponse = await GetResponseForAdvancedQueryService(
    queryWithoutOffsetAndLimit,
    cachedMasterDataApiService
  );
  const formattedResponse = {
    dataPoints: fullDataResponse.dataPoints.map((dp) =>
      formatDataPoint(dp, { referenceCurrency })
    ),
  };
  return formattedResponse;
};

const getFullDataForExportFromQueries = async (
  queries: ApiMasterDataAdvancedQuery[],
  cachedMasterDataApiService: CachedMasterDataService,
  referenceCurrency: Currency | null
): Promise<ExportData> => {
  const queriesWithoutOffsetAndLimit = queries.map((q) => {
    const { limit, offset, ...qNoLimitOrOffset } = q;
    return qNoLimitOrOffset;
  });
  const queriesResponse = await GetResponsesForAdvancedQueriesService(
    queriesWithoutOffsetAndLimit,
    cachedMasterDataApiService
  );
  const fullDataResponse = {
    dataPoints: queriesResponse.responseListItems.flatMap(
      (l) => l.response.dataPoints
    ),
  };
  const formattedResponse = {
    dataPoints: fullDataResponse.dataPoints.map((dp) =>
      formatDataPoint(dp, { referenceCurrency })
    ),
  };
  return formattedResponse;
};

const getFullDataForExportFromService = async (
  service: DataViewService,
  inputs: Record<string, any>,
  filters: ApiMasterDataQueryFilterItem[],
  latestVersions: Partial<Record<DataTypes, VersionId>>,
  legacyStores: LegacyStore,
  legacyDomainScopedStores: LegacyDomainScopedStore
) => {
  const cacheKey = getServiceCacheKey(service, inputs, filters);
  const response = await appCache.getFromCacheOrRequest(cacheKey, () => {
    return service(
      inputs,
      filters,
      latestVersions,
      legacyStores,
      legacyDomainScopedStores
    );
  });
  return response;
};

interface CommonDataViewConfigFields {
  exportDataGetter?: () => Promise<ExportData>;
  useInfiniteLoader?: boolean;
  useLocalSort?: boolean;
  sortableFieldsWhiteList?: DataFieldWithDataType[];
}

interface DataViewConfigSingleView extends CommonDataViewConfigFields {
  queryWithType: ApiDataViewQueryWithTypeInfo;
  sortOrder?: DataFieldWithDataType[];
}

interface DataViewConfigWithTabs {
  tabConfigs: TabConfig[];
  defaultTabIndex?: number;
  onTabChange?: (value: number) => void;
}

export enum TabConfigType {
  TabConfigWithQueries = 'TabConfigWithQueries',
  TabConfigWithService = 'TabConfigWithService',
}
export type TabConfig =
  | {
      type: TabConfigType.TabConfigWithQueries;
      title?: string;
      value: TabConfigWithQueries;
    }
  | {
      type: TabConfigType.TabConfigWithService;
      title?: string;
      value: TabConfigWithService;
    };

type ExportData = {
  dataPoints: {
    dimensions: {
      dataType: ApiMasterDataTypes;
      property: string;
      value: string | number;
    }[];
  }[];
};

interface TabConfigWithQueries extends CommonDataViewConfigFields {
  sortOrder?: DataFieldWithDataType[];
  queriesWithType: ApiDataViewQueryWithTypeInfo[];
  resultsFormatter?: (
    responses: ApiMasterDataQueryResponse[]
  ) => ApiMasterDataQueryResponse;
}
type DataViewServiceResponse = ApiMasterDataQueryResponse;
export type DataViewService = Service<any, DataViewServiceResponse>;
interface TabConfigWithService extends CommonDataViewConfigFields {
  inputs: Record<string, any>;
  filters: ApiMasterDataQueryFilterItem[];
  service: DataViewService;
  sortOrder?: DataFieldWithDataType[];
}

export interface SortConfig {
  sortBy?: DataFieldWithDataType;
  sortOrder: ApiMasterDataQuerySortOrder;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: '100%', width: '100%', overflowX: 'auto' }}
    >
      {value === index && children}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const DashboardDataView = observer((props: DashboardDataViewProps) => {
  const latestEmployeeVersion = useLatestEmployeeVersionContext();
  const legacyDomainScopeStores = useLegacyDomainScopedStoresContext();
  const { reportStore, timeSliderStore } = legacyDomainScopeStores;
  const { timeSliderConfig } = timeSliderStore;
  const { endDate, firstMonthOfYear } = timeSliderConfig;

  const { config, dashboard } = props;
  const globalFilterHandle = useGlobalFilterContext();
  const [filtersState] = globalFilterHandle;
  const { items: filters } = filtersState;
  const dataViewFields = reportStore.getDataViewFields(dashboard);

  const defaultSortingOrder = {
    sortBy: undefined,
    sortOrder: ApiMasterDataQuerySortOrder.ASC,
  };

  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSortingOrder);

  const generatedReportQuery: ApiDataViewQueryWithTypeInfo = {
    type: ApiDataQueryType.ApiMasterDataAdvancedQuery,
    query: {
      dimensions: toJS(dataViewFields).map((dataViewField) =>
        toAdvancedDimension(dataViewField)
      ),
      filterItems: [
        ...combineFilters([...toJS(filters)]),
        getVersionFilterFromDate(
          dateManagerService.getInstance().parseApiDate(endDate),
          firstMonthOfYear,
          latestEmployeeVersion
        ),
        PRESENT_TEMPORALITY_FILTER,
      ],
      dataType: DataTypes.EMPLOYEE,
    },
  };

  const finalConfig: DataViewConfigWithType = config ?? {
    type: DataViewConfigType.DataViewConfigSingleView,
    dataViewConfig: { queryWithType: generatedReportQuery },
  };

  return match(finalConfig)
    .with({ type: DataViewConfigType.DataViewConfigSingleView }, (c) => (
      <DashboardDataViewSingleView
        config={getQueryWithSortParamsForSingleView(
          c.dataViewConfig,
          sortConfig
        )}
        dashboard={dashboard}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
      />
    ))
    .with({ type: DataViewConfigType.DataViewConfigWithTabs }, (c) => (
      <DashboardDataViewTabsView
        config={c.dataViewConfig}
        dashboard={dashboard}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
      />
    ))
    .exhaustive();
});

interface DashboardDataViewSingleViewProps {
  config: DataViewConfigSingleView;
  dashboard: Dashboards;
  setSortConfig: (newSortConfig: SortConfig) => void;
  sortConfig: SortConfig;
}

const DashboardDataViewSingleView = observer(
  (props: DashboardDataViewSingleViewProps) => {
    const aliases = useAliasesContext();
    const locale = useGlobalLocaleContext();
    const selectedLanguage = locale.selected;
    const { dashboards: allDashboards, isLoading } =
      useListDomainViewDashboards();
    const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
    const {
      cachedMasterDataApiService,
      reportStore,
      exportStore,
      companySettingsStore,
    } = legacyDomainScopedStores;
    const { config, dashboard, setSortConfig, sortConfig } = props;

    const { useInfiniteLoader = true, useLocalSort } = config;
    const useServerSort = useInfiniteLoader && useLocalSort !== true;
    const referenceCurrency = companySettingsStore.referenceCurrency();
    if (isLoading) {
      return (
        <BetterLoadingOverlay
          debugString="DashboardDataViewSingleView"
          isPending={isLoading}
        />
      );
    }
    return (
      <QueryExecutor
        key={`dashboard-report-view-1`}
        queryConfig={toJS({
          queriesWithType: config.queryWithType ? [config.queryWithType] : [],
        })}
        withOffset={useInfiniteLoader}
      >
        {DataViewComponent({
          dashboard,
          setSortConfig: useServerSort ? setSortConfig : undefined,
          sortConfig: useServerSort ? sortConfig : undefined,
          exportDataGetter:
            config.exportDataGetter ??
            (() =>
              getFullDataForExportFromQuery(
                config.queryWithType?.query as ApiMasterDataAdvancedQuery,
                cachedMasterDataApiService,
                referenceCurrency
              )),
          sortOrder: config.sortOrder,
          allDashboards,
          aliases,
          selectedLanguage,
          reportStore,
          exportStore,
        })}
      </QueryExecutor>
    );
  }
);

interface DashboardDataViewTabsViewProps {
  config: DataViewConfigWithTabs;
  dashboard: Dashboards;
  setSortConfig: (newSortConfig: SortConfig) => void;
  sortConfig: SortConfig;
}

const DashboardDataViewTabsView = observer(
  (props: DashboardDataViewTabsViewProps) => {
    const aliases = useAliasesContext();
    const locale = useGlobalLocaleContext();
    const selectedLanguage = locale.selected;
    const { dashboards: allDashboards, isLoading } =
      useListDomainViewDashboards();
    const legacyStores = useLegacyStoresContext();
    const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
    const {
      reportStore,
      cachedMasterDataApiService,
      exportStore,
      timeSliderStore,
      companySettingsStore,
    } = legacyDomainScopedStores;
    const latestVersions = useLatestVersionsContext();
    const { t } = useTranslation();
    const { config, dashboard, setSortConfig, sortConfig } = props;
    const [activeTabIndex, handleTabChange] = useTabs(
      config.tabConfigs.length,
      config.defaultTabIndex
    );
    const handleReactTabChange = (_e: ChangeEvent<{}>, value: number) => {
      setSortConfig({
        sortBy: undefined,
        sortOrder: ApiMasterDataQuerySortOrder.ASC,
      });
      config.onTabChange && config.onTabChange(value);

      handleTabChange(value);
    };

    const referenceCurrency = companySettingsStore.referenceCurrency();

    if (isLoading) {
      return (
        <BetterLoadingOverlay
          debugString="DashboardDataViewTabsView"
          isPending={isLoading}
        />
      );
    }
    return (
      <>
        {config.tabConfigs.length > 1 && (
          <StyledAppBar position="static">
            <Tabs
              value={activeTabIndex}
              onChange={handleReactTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {config.tabConfigs.map((tabConfig, index) => {
                return (
                  <Tab
                    label={
                      tabConfig.title ? t(tabConfig.title) : `Tab ${index}`
                    }
                    key={`dashboard-report-view-tab-${index}`}
                    {...a11yProps(index)}
                  />
                );
              })}
            </Tabs>
          </StyledAppBar>
        )}
        {config.tabConfigs.map((tabConfig, index) => {
          return (
            <TabPanel
              value={activeTabIndex}
              index={index}
              key={tabConfig.title ?? index}
            >
              {match(tabConfig)
                .with({ type: TabConfigType.TabConfigWithQueries }, (c) => {
                  const {
                    useInfiniteLoader = true,
                    useLocalSort,
                    sortableFieldsWhiteList,
                  } = c.value;
                  const useServerSort =
                    useInfiniteLoader && useLocalSort !== true;
                  return (
                    <QueryExecutor
                      key={`dashboard-report-view-${index}`}
                      queryConfig={getQueriesWithSortParamsForTabsView(
                        toJS(c.value),
                        sortConfig
                      )}
                      withOffset={useInfiniteLoader}
                    >
                      {DataViewComponent({
                        dashboard,
                        setSortConfig: useServerSort
                          ? setSortConfig
                          : undefined,
                        sortConfig: useServerSort ? sortConfig : undefined,
                        sortableFieldsWhiteList,
                        exportDataGetter:
                          c.value.exportDataGetter ??
                          (() =>
                            getFullDataForExportFromQueries(
                              c.value.queriesWithType.map(
                                (q) => q.query
                              ) as ApiMasterDataAdvancedQuery[],
                              cachedMasterDataApiService,
                              referenceCurrency
                            )),
                        sortOrder: c.value.sortOrder,
                        allDashboards,
                        aliases,
                        selectedLanguage,
                        reportStore,
                        exportStore,
                      })}
                    </QueryExecutor>
                  );
                })
                .with({ type: TabConfigType.TabConfigWithService }, (c) => {
                  const { inputs, filters, service, sortOrder } = c.value;
                  const { startDate, endDate } =
                    timeSliderStore.timeSliderConfig;
                  const {
                    useInfiniteLoader = true,
                    useLocalSort,
                    sortableFieldsWhiteList,
                  } = c.value;
                  const useServerSort =
                    useInfiniteLoader && useLocalSort !== true;
                  return (
                    <ServiceExecutorWithRoles
                      key={`dashboard-report-view-${index}`}
                      inputs={toJS({
                        ...inputs,
                        sortConfig,
                        startDate,
                        endDate,
                      })}
                      filters={toJS(filters)}
                      service={service}
                      withOffset={useInfiniteLoader}
                    >
                      {DataViewComponent({
                        dashboard,
                        setSortConfig: useServerSort
                          ? setSortConfig
                          : undefined,
                        sortConfig: useServerSort ? sortConfig : undefined,
                        sortableFieldsWhiteList,
                        exportDataGetter:
                          c.value.exportDataGetter ??
                          (() =>
                            getFullDataForExportFromService(
                              service,
                              { ...inputs, startDate, sortConfig, endDate },
                              filters,
                              latestVersions,
                              legacyStores,
                              legacyDomainScopedStores
                            )),
                        sortOrder,
                        allDashboards,
                        aliases,
                        selectedLanguage,
                        reportStore,
                        exportStore,
                      })}
                    </ServiceExecutorWithRoles>
                  );
                })
                .exhaustive()}
            </TabPanel>
          );
        })}
      </>
    );
  }
);

interface DataViewComponentInputs {
  dashboard: Dashboards;
  setSortConfig?: (newSortConfig: SortConfig) => void;
  sortConfig?: SortConfig;
  exportDataGetter: () => Promise<ExportData>;
  sortOrder?: DataFieldWithDataType[];
  sortableFieldsWhiteList?: DataFieldWithDataType[];
  allDashboards: Dashboard[];
  aliases: ApiAlias[];
  selectedLanguage: Languages;
  reportStore: ReportStore;
  exportStore: ExportStore;
}

const DataViewComponent =
  ({
    dashboard,
    setSortConfig,
    sortConfig,
    exportDataGetter,
    sortOrder,
    sortableFieldsWhiteList,
    allDashboards,
    aliases,
    selectedLanguage,
    reportStore,
    exportStore,
  }: DataViewComponentInputs): ((state: RenderCallback) => React.ReactNode) =>
  ({ data, refetch }) => {
    if (!data) {
      return (
        <BetterLoadingOverlay
          debugString="DataViewComponent"
          isPending={!data}
        />
      );
    }
    const { dataPoints } = data;
    if (!dataPoints || dataPoints.length === 0) {
      return (
        <StyledTypography variant="h6">
          <Trans i18nKey="common:commonValues.noDataMessage" />
        </StyledTypography>
      );
    }

    const headers: DataFieldWithDataType[] =
      dataPoints[0]?.dimensions
        ?.map((dim) => dimensionToDataFieldWithDataType(dim))
        ?.filter((d) => d.dataField !== EmployeeDataFields.VERSION_ID) ?? [];

    const exportDataCallback = () => {
      void exportDataGetter().then((data) =>
        exportDataViewData(
          data,
          dashboard,
          allDashboards,
          aliases,
          selectedLanguage,
          reportStore,
          exportStore
        )
      );
    };

    return (
      <ReportContainer>
        {'recordsTotal' in data ? (
          <>
            {i18n.t('common:dataView.recordsWidget', {
              recordsLoaded: data.recordsLoaded,
              recordsTotal: data.recordsTotal,
            })}
          </>
        ) : (
          ''
        )}
        <ReportView
          dataPoints={dataPoints}
          headers={headers}
          loadMoreRows={refetch}
          sortOrder={sortOrder}
          setSortConfig={setSortConfig}
          sortConfig={sortConfig}
          exportDataCallback={exportDataCallback}
          sortableFieldsWhiteList={sortableFieldsWhiteList}
        />
      </ReportContainer>
    );
  };
